import { useMutation, useQueryClient } from '@tanstack/react-query';
import QualAPI from 'utils/QualAPI';
import { codeKeys } from '../queries'
import { normalizeId } from 'hooks/utils/idNormalization';
import { createCodeCacheUtils } from '../../utils/codeCacheUtils';


/**
 * Hook for deleting a code with optimistic updates
 * Handles re-parenting of child codes to the deleted code's parent
 * @returns {Object} The mutation object with mutate function and state
 */
export function useDeleteCode() {
  const queryClient = useQueryClient();
  const cacheUtils = createCodeCacheUtils(queryClient);
  
  return useMutation({
    mutationFn: ({ codeId }) => QualAPI.deleteCode(codeId).then(response => response.data),
    
    onMutate: async (variables) => {
      const { codeId, projectId } = variables;
      const normalizedCodeId = normalizeId(codeId);
      const normalizedProjectId = normalizeId(projectId);
      
      // Cancel any outgoing queries for the project codes list and code detail
      await queryClient.cancelQueries({ queryKey: codeKeys.list(normalizedProjectId) });
      await queryClient.cancelQueries({ queryKey: codeKeys.detail(normalizedCodeId) });
      
      // Get current project codes data
      const previousProjectCodes = queryClient.getQueryData(codeKeys.list(normalizedProjectId));
      
      if (previousProjectCodes?.codes) {
        // Find the code being deleted
        const deletedCode = previousProjectCodes.codes.find(code => 
          normalizeId(code.id) === normalizedCodeId
        );
        
        if (deletedCode) {
          const parentId = deletedCode.parent_id || null;
          const deletedPosition = deletedCode.position || 1;
          
          // Find all children of the deleted code
          const children = previousProjectCodes.codes.filter(code => 
            normalizeId(code.parent_id) === normalizedCodeId
          ).sort((a, b) => (a.position || 0) - (b.position || 0));
          
          // Update the codes array:
          let updatedCodes = previousProjectCodes.codes.map(code => {
            const codeIdNorm = normalizeId(code.id);
            
            // Skip the deleted code
            if (codeIdNorm === normalizedCodeId) {
              return null;
            }
            
            // Re-parent children of deleted code
            if (normalizeId(code.parent_id) === normalizedCodeId) {
              const childIndex = children.findIndex(c => normalizeId(c.id) === codeIdNorm);
              const updatedChild = {
                ...code,
                parent_id: parentId,
                position: deletedPosition + childIndex
              };
              // Update individual cache for the child
              cacheUtils.updateCode(codeIdNorm, {
                parent_id: parentId,
                position: updatedChild.position
              }, { skipProjectList: true });
              return updatedChild;
            }
            
            // Shift positions of siblings that come after the deleted code and its children
            if ((code.parent_id || null) === parentId && 
                (code.position || 0) > deletedPosition) {
              const updatedCode = {
                ...code,
                position: (code.position || 0) + children.length - 1
              };
              // Only update if position actually changed
              if (updatedCode.position !== code.position) {
                cacheUtils.updateCode(codeIdNorm, {
                  position: updatedCode.position
                }, { skipProjectList: true });
              }
              return updatedCode;
            }
            
            return code;
          }).filter(Boolean); // Remove the deleted code (null)
          
          queryClient.setQueryData(codeKeys.list(normalizedProjectId), {
            ...previousProjectCodes,
            codes: updatedCodes
          });
        } else {
          // Fallback: just remove the code if we can't find it
          const updatedCodes = previousProjectCodes.codes.filter(code => 
            normalizeId(code.id) !== normalizedCodeId
          );
          
          queryClient.setQueryData(codeKeys.list(normalizedProjectId), {
            ...previousProjectCodes,
            codes: updatedCodes
          });
        }
      }
      
      // Remove the code detail from cache
      // setting to null forces a refresh of the components
      queryClient.setQueryData(codeKeys.detail(normalizedCodeId), null);
      
      return { 
        normalizedProjectId, 
        normalizedCodeId
      };
    },
    
    onSuccess: (data, variables, context) => {
      const normalizedCodeId = context?.normalizedCodeId ?? normalizeId(variables.codeId);
      // Scrub the deleted code's codings from every cached excerpt (detail
      // caches and transcript bulk copies) so chip renderers and the
      // comparison overlay stop showing a dead id before refetches land.
      cacheUtils.removeCodeFromExcerpts(normalizedCodeId);
    },

    onError: (err, variables, context) => {
      const { codeId } = variables;
      const normalizedCodeId = normalizeId(codeId);

      // If we have context, use those normalized IDs, otherwise derive from variables
      const projectIdToInvalidate = context?.normalizedProjectId;
      const codeIdToInvalidate = context?.normalizedCodeId || normalizedCodeId;
      
      // If we have the project ID, invalidate the list
      if (projectIdToInvalidate) {
        queryClient.invalidateQueries({ queryKey: codeKeys.list(projectIdToInvalidate) });
      }
      
      // Always invalidate the code detail
      queryClient.invalidateQueries({ queryKey: codeKeys.detail(codeIdToInvalidate) });
      
      console.error('Error deleting code:', err);
    },
    
    onSettled: (data, error, variables, context) => {
      if (context) {
        const { normalizedProjectId, normalizedCodeId } = context;
        
        // Invalidate queries to ensure fresh data
        queryClient.invalidateQueries({ queryKey: codeKeys.list(normalizedProjectId) });
        queryClient.invalidateQueries({ queryKey: codeKeys.detail(normalizedCodeId) });
      }
    }
  });
}